import { faArrowTrendUp, faMemoPad, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    FavoriteType,
    ICorporationResponse,
    IDashboardResponse,
    ILocationResponse,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { CreateButton } from 'components/button/CreateButton';
import { DocumentTitle } from 'components/DocumentTitle';
import { DashboardTabs } from 'components/dashboard/shared';
import { NoFavorites } from 'components/favorites/NoFavorites';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { CreateDashboardModal } from 'components/modal/CreateDashboardModal';
import { DashboardSettingsModal } from 'components/modal/DashboardSettingsModal';
import { BasePage } from 'components/page/BasePage';
import { Tab, TabList, Tabs } from 'components/tabs/Tabs';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useUser } from 'contexts/UserContext';
import {
    DashboardsPagedDataSource,
    useDashboardsPagedDataSource,
} from 'custom_hooks/data_hooks/data_source/dashboardsDataSource';
import { MediaSize, useIsViewportAtLeast } from 'custom_hooks/useWindowSize';
import { DashboardsTable } from 'pages/Dashboard/DashboardsTable';
import { useState } from 'react';
import { TabPanel } from 'react-tabs';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { getCorporationSpecificDashboardRoute } from 'utils/RouteHelper';
import { DashboardTile, SvgGradientDefs } from './DashboardTile';

const DashboardTable = styled.div`
    padding-top: 0;
`;

const StyledTabList = styled(TabList)`
    margin-bottom: 2rem;
`;

type DashboardTableSectionProps = Readonly<{
    dataSource: DashboardsPagedDataSource;
    tab: DashboardTabs;
    hideWhenEmpty?: boolean;
    headerText: string;
    headerId: string;
    onSettings: (dashboard: IDashboardResponse) => void;
    onDelete: (dashboard: IDashboardResponse) => void;
}>;

function DashboardTableSection({
    dataSource,
    tab,
    hideWhenEmpty = false,
    headerText,
    headerId,
    onSettings,
    onDelete,
}: DashboardTableSectionProps) {
    if (hideWhenEmpty && (!dataSource.results || dataSource.results.length === 0)) {
        return null;
    }

    return (
        <DashboardTable aria-labelledby={headerId}>
            <span id={headerId} role="heading" aria-level={2} className="f-18 bold">
                {headerText}
            </span>
            <DashboardsTable
                className="mt-20"
                dataSource={dataSource}
                tab={tab}
                onSettings={onSettings}
                onDelete={onDelete}
            />
        </DashboardTable>
    );
}

export type DashboardLandingPageProps = Readonly<{
    corporation: ICorporationResponse;
    location?: ILocationResponse;
}>;

export function DashboardLandingPage({
    corporation,
    location,
}: DashboardLandingPageProps) {
    const { showToast } = useNotificationContext();
    const showCreateButton = useIsViewportAtLeast(MediaSize.large);
    const { canCreateDashboards } = usePermissions();
    const [tab, setTab] = useState(DashboardTabs.AllDashboards);
    const { favorites, updateFavorites } = useUser();

    const powerBiDashboardsDataSource = useDashboardsPagedDataSource({
        name: 'dashboards-table',
        defaultFilter: {
            corporationId: corporation.Id,
            locationId: location?.Id,
        },
    });

    const [selectedDashboard, setSelectedDashboard] = useState<IDashboardResponse>();
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [newDashboardModalVisible, setNewDashboardModalVisible] = useState(false);

    const dashboardSettingsClicked = (dashboard: IDashboardResponse) => {
        setSelectedDashboard(dashboard);
        setSettingsModalOpen(true);
    };

    const dashboardDeleteClicked = (dashboard: IDashboardResponse) => {
        setSelectedDashboard(dashboard);
        setDeleteModalOpen(true);
    };

    const onDashboardCreate = () => {
        powerBiDashboardsDataSource.reloadPage();
        setNewDashboardModalVisible(false);
    };

    const noFavorites = !favorites?.some((f) =>
        f.FavoriteType.FavoriteTypeName.endsWith('Dashboard'),
    );

    const noPowerBiFavorites = !favorites?.some(
        (f) => f.FavoriteType.FavoriteTypeName === FavoriteType.PowerBiDashboard,
    );

    const { mutate: deleteDashboard } = useMutation({
        mutationFn: async () => {
            if (!selectedDashboard) {
                throw new Error(
                    'Tried to delete dashboard without selectedDashboard being set',
                );
            }

            await sdClient.deleteDashboard({ id: selectedDashboard.Id });
            await updateFavorites();
        },

        onSuccess: () => {
            onDeleteDashboardClose();
            powerBiDashboardsDataSource.reloadPage();
            showToast({ type: 'success', content: 'Successfully deleted dashboard' });
        },

        onError: () =>
            showToast({ type: 'error', content: 'Failed to delete dashboard' }),
    });

    const onDeleteDashboardClose = () => {
        setSelectedDashboard(undefined);
        setDeleteModalOpen(false);
    };

    return (
        <BasePage className="tile-page">
            <DocumentTitle title={`${location?.Name ?? corporation.Name} Dashboards`} />
            <Tabs selectedIndex={tab} onSelect={(index) => setTab(index)}>
                <StyledTabList className="flex align-center f-20">
                    <Tab>All Dashboards</Tab>
                    <Tab>
                        <FontAwesomeIcon
                            className="pr-5 mb-2"
                            color={tokens.misc.favorite}
                            icon={faStar}
                        />
                        Favorites
                    </Tab>
                    {canCreateDashboards && showCreateButton && (
                        <CreateButton
                            className="ml-auto"
                            onClick={() =>
                                setNewDashboardModalVisible(!newDashboardModalVisible)
                            }
                        />
                    )}
                </StyledTabList>

                <SvgGradientDefs />

                <div>
                    <span
                        id="corporate-value-dashboards-header"
                        role="heading"
                        aria-level={2}
                        className="f-18 bold"
                    >
                        Corporate Value Dashboards
                    </span>

                    <div className="tile-page-grid mt-20 mb-20">
                        <DashboardTile
                            icon={faArrowTrendUp}
                            navigateTo={getCorporationSpecificDashboardRoute(
                                corporation.Id,
                                'deployment-and-value',
                            )}
                            title="Deployment and Value"
                            subtitle="Sensor deployments and value delivered across all Sites"
                            variant="gradient-bg"
                        />

                        <DashboardTile
                            icon={faMemoPad}
                            navigateTo={getCorporationSpecificDashboardRoute(
                                corporation.Id,
                                'desk-activity-overview',
                            )}
                            title="Desk Activity"
                            subtitle="Issues detected and addressed across all Sites"
                            variant="gradient-bg"
                        />
                    </div>
                </div>

                <TabPanel>
                    <DashboardTableSection
                        dataSource={powerBiDashboardsDataSource}
                        tab={tab}
                        headerText="Sentry Dashboards"
                        headerId="all-dashboards-header"
                        onSettings={dashboardSettingsClicked}
                        onDelete={dashboardDeleteClicked}
                    />
                </TabPanel>
                <TabPanel>
                    {!noPowerBiFavorites && (
                        <DashboardTableSection
                            dataSource={powerBiDashboardsDataSource}
                            tab={tab}
                            hideWhenEmpty
                            headerText="Sentry Dashboards"
                            headerId="favorite-dashboards-header"
                            onSettings={dashboardSettingsClicked}
                            onDelete={dashboardDeleteClicked}
                        />
                    )}
                    {noFavorites && (
                        <NoFavorites favoriteType={FavoriteType.PowerBiDashboard} />
                    )}
                </TabPanel>
            </Tabs>
            {/* portals */}
            {deleteModalOpen && selectedDashboard && (
                <ConfirmModal
                    title={`Delete '${selectedDashboard.Name}'`}
                    actionText="delete this dashboard? Other users in your organization may be referencing it."
                    checkText="delete this dashboard for all users"
                    actionButtonText="Delete"
                    onAction={deleteDashboard}
                    onClose={onDeleteDashboardClose}
                />
            )}
            {settingsModalOpen && (
                <DashboardSettingsModal
                    corporation={corporation}
                    location={location}
                    dashboard={selectedDashboard}
                    onCancel={() => setSettingsModalOpen(false)}
                    onSave={() => powerBiDashboardsDataSource.reloadPage()}
                />
            )}
            {newDashboardModalVisible && (
                <CreateDashboardModal
                    onCancel={() => setNewDashboardModalVisible(false)}
                    onCreate={onDashboardCreate}
                    location={location}
                    corporation={corporation}
                />
            )}
        </BasePage>
    );
}
